import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Record as RRecord,
  Static,
  String,
  Union,
} from "runtypes";
import { z } from "zod";

import { ExtractAtomicOperationFromDefinition } from "../../atomic-operations/index.js";
import { BlockConfig } from "../../blockCell/index.js";
import { DEFAULT_CHART_RESULT_VARIABLE } from "../../chart/chartConstants.js";
import { getChartReferencedDataFrames } from "../../chart/chartDataFrameUtils.js";
import { newSuggestibleChart } from "../../chart/createChartDefaults.js";
import {
  ChartColorMappings,
  ChartSelection,
  ChartSpec,
} from "../../chart/types.js";
import {
  CellReferencesParseError,
  CellReferencesV2,
  PythonCellReferencesV2,
  SqlCellReferencesV2,
  StoredCellReferencesV3,
  StoredJinjaSqlReferences,
  StoredSqlReferencesV3,
} from "../../code/index.js";
import { UTC } from "../../constants.js";
import {
  DateTimeString,
  TimezoneName,
  getDateTimeString,
  zDateTimeString,
  zTimezoneName,
} from "../../dateTypes.js";
import { SecondaryCalculations } from "../../dbt/generated/dbtSecondaryCalculations.js";
import { SecondaryCalculationsSchema } from "../../dbt/secondaryCalculationsSchema.js";
import {
  DISPLAY_TABLE_DEFAULTS,
  DisplayTableConfigPayload,
  SortDirection,
} from "../../display-table/index.js";
import {
  CellOutputType,
  CellType,
  ChartDisplayType,
  CollapsibleCellLabelStyle,
  MetricCellComparisonFormat,
  MetricCellComparisonType,
} from "../../enums.js";
import { defaultExploreSpec } from "../../explore/exploreDefaults.js";
import { updateExploreSpecFromChartDiffs } from "../../explore/index.js";
import {
  ExploreSpec,
  ExploreViewType,
  ExploreVisualizationType,
} from "../../explore/types.js";
import {
  BASE_FILTER_GROUP,
  FilterCellAppMode,
  FilterGroup,
  FilterType,
} from "../../filter/index.js";
import {
  BlockCellId,
  CellGroupCellId,
  CellId,
  ChartCellId,
  CodeCellId,
  CollapsibleCellId,
  ComponentImportCellId,
  DataConnectionId,
  DataSourceDbtMetricId,
  DbtMetricCellId,
  DisplayTableCellId,
  DisplayTableConfigId,
  ExploreCellId,
  FilterCellId,
  HexVersionId,
  MapCellId,
  MarkdownCellId,
  MetricCellId,
  ParameterId,
  PivotCellId,
  SemanticDatasetName,
  SemanticProjectId,
  SemanticViewName,
  SqlCellId,
  StoryElementId,
  TextCellId,
  VegaChartCellId,
  WritebackCellId,
} from "../../idTypeBrands.js";
import { Map, MapThemeHex } from "../../map/mapTypes.js";
import { MetricCellAggregate } from "../../metric/metricCellAggregate.js";
import { MetricDisplayFormat } from "../../metric/metricDisplayFormatTypes.js";
import {
  ParameterInputMetadata,
  ParameterInputType,
  ParameterOptions,
  ParameterOutputType,
} from "../../parameterType.js";
import { PivotTableConfig } from "../../pivot/pivotTypes.js";
import { convertRichTextFromPlainText } from "../../richText.js";
import { RichTextDocument } from "../../richTextTypes.js";
import { SemanticCap } from "../../semanticCaps.js";
import {
  DataSourceTableConfig,
  ExploreCellDataframe,
  NoCodeCellDataframe,
} from "../../sql/dataSourceTableConfig.js";
import {
  JsonSerializedParameterValueContents,
  ParameterName,
  zJsonSerializedParameterValueContents,
  zParameterName,
} from "../../typeBrands.js";
import { uuid } from "../../uuid.js";
import { VegaLiteSpec, VegaMetadata } from "../../vega-chart-cell/types.js";
import { newDefaultTopLevelLayerSpec } from "../../vega-chart-cell/vegaDefaultSpec.js";
import { createHexVersionAtomicOperationDefinition } from "../HexVersionAtomicOperationDefinition.js";

import { HqlAggregationFunction } from "../../hql/types.js";
import { zFromRuntype, zUnvalidatedObject } from "../../zod/utils.js";
import {
  zBlockCellId,
  zCellGroupCellId,
  zCellId,
  zChartCellId,
  zCodeCellId,
  zCollapsibleCellId,
  zComponentImportCellId,
  zDataConnectionId,
  zDataSourceDbtMetricId,
  zDbtMetricCellId,
  zDisplayTableCellId,
  zFilterCellId,
  zHexVersionId,
  zMagicEventId,
  zMapCellId,
  zMarkdownCellId,
  zMetricCellId,
  zParameterId,
  zPivotCellId,
  zSqlCellId,
  zStaticCellId,
  zStoryElementId,
  zTextCellId,
  zVegaChartCellId,
  zWritebackCellId,
} from "../../zod/ZodIdTypeBrands.js";
import { MAP_DEFAULT_HEIGHT } from "./map/mapConstants.js";

const zCreateOriginMetadata = z.enum([
  "ADD_CELL_BAR",
  "MULTISELECT_BAR",
  "CELL_ACTION_MENU",
]);
export type CreateOriginMetadata = z.infer<typeof zCreateOriginMetadata>;

export interface CodeCellPayload {
  type: (typeof CellType)["CODE"];
  id: CodeCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  source: string;
  cellReferencesV2: PythonCellReferencesV2 | null;
  cellReferencesParseError: CellReferencesParseError | null;
}

export function createCodeCellPayload({
  cellReferencesParseError,
  cellReferencesV2,
  id = uuid() as CodeCellId,
  source,
}: {
  id?: CodeCellId;
  source?: string;
  cellReferencesV2?: PythonCellReferencesV2 | null;
  cellReferencesParseError?: CellReferencesParseError | null;
} = {}): CodeCellPayload {
  return {
    type: CellType.CODE,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    source: source ?? "",
    cellReferencesV2: cellReferencesV2 ?? null,
    cellReferencesParseError: cellReferencesParseError ?? null,
  };
}

export interface MarkdownCellPayload {
  type: (typeof CellType)["MARKDOWN"];
  id: MarkdownCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  source: string;
  cellReferencesV2: CellReferencesV2 | null;
  cellReferencesParseError: CellReferencesParseError | null;
}

export function createMarkdownCellPayload({
  cellReferencesParseError,
  cellReferencesV2,
  id = uuid() as MarkdownCellId,
  source,
}: {
  id?: MarkdownCellId;
  source?: string;
  cellReferencesV2?: CellReferencesV2 | null;
  cellReferencesParseError?: CellReferencesParseError | null;
} = {}): MarkdownCellPayload {
  return {
    type: CellType.MARKDOWN,
    id: id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    source: source ?? "",
    cellReferencesV2: cellReferencesV2 ?? null,
    cellReferencesParseError: cellReferencesParseError ?? null,
  };
}

export interface TextCellPayload {
  type: (typeof CellType)["TEXT"];
  id: TextCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  richText: RichTextDocument;
  cellReferencesV2: CellReferencesV2 | null;
  cellReferencesParseError: CellReferencesParseError | null;
}

export function createTextCellPayload({
  cellReferencesParseError,
  cellReferencesV2,
  id = uuid() as TextCellId,
  richText,
}: {
  id?: TextCellId;
  richText?: RichTextDocument;
  cellReferencesV2?: CellReferencesV2 | null;
  cellReferencesParseError?: CellReferencesParseError | null;
} = {}): TextCellPayload {
  return {
    type: CellType.TEXT,
    id: id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    richText: richText ?? convertRichTextFromPlainText(""),
    cellReferencesV2: cellReferencesV2 ?? null,
    cellReferencesParseError: cellReferencesParseError ?? null,
  };
}

export interface CellGroupCellPayload {
  type: (typeof CellType)["CELL_GROUP"];
  id: CellGroupCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  runOnAppLoad: boolean;
}

export interface CreateDisplayTableCellMandatoryFields {
  resultVariable: string;
}

export interface DisplayTableCellPayload {
  type: (typeof CellType)["DISPLAY_TABLE"];
  id: DisplayTableCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  /**
   * @deprecated in favor of `dataframe
   */
  dataFrameVariableName?: string;
  dataframe: NoCodeCellDataframe | null;
  resultVariable: string;
  displayTableConfig: DisplayTableConfigPayload;
}

export function createDisplayTableCellPayload({
  dataframe = null,
  displayTableConfig,
  displayTableConfigId = uuid() as DisplayTableConfigId,
  id = uuid() as DisplayTableCellId,
  pageSize,
  resultVariable,
}: {
  id?: DisplayTableCellId;
  dataframe?: NoCodeCellDataframe | null;
  displayTableConfigId?: DisplayTableConfigId;
  displayTableConfig?: Partial<DisplayTableConfigPayload>;
  pageSize?: number;
  resultVariable?: string;
} = {}): DisplayTableCellPayload {
  return {
    type: CellType.DISPLAY_TABLE,
    id: id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    dataframe,
    resultVariable: resultVariable ?? "",
    displayTableConfig: {
      pageSize: pageSize ?? DISPLAY_TABLE_DEFAULTS.pageSize,
      hideIcons: false,
      hideIndex: false,
      defaultColumnWidth: null,
      sortDirectionDefault: SortDirection.ASC,
      columnProperties: [],
      pinIndexColumns: false,
      ...displayTableConfig,
      revision: 0,
      id: displayTableConfigId,
    },
  };
}

export interface CreateMetricCellMandatoryFields {
  valueResultVariable: string | undefined;
  comparisonResultVariable: string | undefined;
}

export interface MetricCellPayload {
  type: (typeof CellType)["METRIC"];
  id: MetricCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  title: string | undefined;
  valueVariableName: string | undefined;
  displayFormat: MetricDisplayFormat | undefined;
  showComparison: boolean;
  comparisonType: MetricCellComparisonType | undefined;
  comparisonVariableName: string | undefined;
  comparisonLabel: string | undefined;
  comparisonFormat?: MetricCellComparisonFormat;
  valueRowIndex: number | undefined;
  valueColumn: string | undefined;
  valueAggregate: MetricCellAggregate | undefined;
  comparisonColumn: string | undefined;
  comparisonRowIndex: number | undefined;
  comparisonAggregate: MetricCellAggregate | undefined;
  valueResultVariable: string | undefined;
  comparisonResultVariable: string | undefined;
  outputResult: boolean | undefined;
  cellReferencesV2: CellReferencesV2 | null;
  cellReferencesParseError: CellReferencesParseError | null;
}

export function createMetricCellPayload({
  cellReferencesParseError,
  cellReferencesV2,
  comparisonAggregate,
  comparisonColumn,
  comparisonFormat,
  comparisonLabel,
  comparisonResultVariable,
  comparisonRowIndex,
  comparisonType,
  comparisonVariableName,
  displayFormat,
  id = uuid() as MetricCellId,
  outputResult,
  showComparison,
  title,
  valueAggregate,
  valueColumn,
  valueResultVariable,
  valueRowIndex,
  valueVariableName,
}: {
  id?: MetricCellId;
  title?: string;
  valueVariableName?: string;
  displayFormat?: MetricDisplayFormat;
  showComparison?: boolean;
  comparisonType?: MetricCellComparisonType;
  comparisonVariableName?: string;
  comparisonFormat?: MetricCellComparisonFormat;
  comparisonLabel?: string;
  valueRowIndex?: number;
  valueColumn?: string;
  comparisonColumn?: string;
  comparisonRowIndex?: number;
  valueAggregate?: MetricCellAggregate;
  comparisonAggregate?: MetricCellAggregate;
  valueResultVariable?: string;
  comparisonResultVariable?: string;
  outputResult?: boolean;
  cellReferencesV2?: CellReferencesV2 | null;
  cellReferencesParseError?: CellReferencesParseError | null;
}): MetricCellPayload {
  return {
    type: CellType.METRIC,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    title,
    comparisonFormat,
    comparisonLabel,
    comparisonType: comparisonType ?? MetricCellComparisonType.VALUE,
    comparisonVariableName,
    displayFormat,
    showComparison: showComparison ?? false,
    valueVariableName,
    valueRowIndex,
    valueColumn,
    comparisonColumn,
    comparisonRowIndex,
    valueAggregate: valueAggregate,
    comparisonAggregate,
    valueResultVariable,
    comparisonResultVariable,
    outputResult,
    cellReferencesV2: cellReferencesV2 ?? null,
    cellReferencesParseError: cellReferencesParseError ?? null,
  };
}

export interface InputCellPayload {
  type: (typeof CellType)["INPUT"];
  id: ParameterId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  name: ParameterName;
  outputType: ParameterOutputType;
  inputType: ParameterInputType;
  options?: ParameterOptions | null;
  defaultValueString?: JsonSerializedParameterValueContents;
  required: boolean;
}

export function createInputCellPayload({
  defaultValueString,
  id = uuid() as ParameterId,
  inputType,
  name,
  options,
  outputType,
}: {
  id?: ParameterId;
  name: ParameterName;
  inputType?: ParameterInputType;
  outputType?: ParameterOutputType;
  defaultValueString?: JsonSerializedParameterValueContents;
  options?: ParameterOptions;
}): InputCellPayload {
  const nonNullInputType = inputType ?? ParameterInputType.TEXT_INPUT;
  const nonNullOutputType =
    outputType ?? ParameterInputMetadata[nonNullInputType].allowedDataTypes[0];
  if (!nonNullOutputType) {
    throw new Error(`No valid output types for input type ${nonNullInputType}`);
  }

  return {
    type: CellType.INPUT,
    id: id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    name,
    inputType: nonNullInputType,
    outputType: nonNullOutputType,
    defaultValueString,
    required: false,
    options,
  };
}

export interface CreateCellHelpers {
  getNewSqlCellFields: () => CreateSqlCellMandatoryFields;
  getNewDbtMetricCellFields: () => CreateDbtMetricCellMandatoryFields;
  getNewPivotCellFields: () => CreatePivotCellMandatoryFields;
  getUniqueInputCellName: () => ParameterName;
  getNewFilterCellFields: () => CreateFilterCellMandatoryFields;
  getNewChartCellFields: () => CreateChartCellMandatoryFields;
  getNewDisplayTableCellFields: () => CreateDisplayTableCellMandatoryFields;
  getNewMetricCellFields: (
    outputResult: boolean,
    additionalExistingNames?: string[],
  ) => CreateMetricCellMandatoryFields;
}

export interface CreateSqlCellMandatoryFields {
  // a new unique result variable that shouldn't conflict
  resultVariable: string;
  // the last used connection id
  connectionId: DataConnectionId | null;
  castDecimals: boolean;
}

export interface SqlCellPayload {
  type: (typeof CellType)["SQL"];
  id: SqlCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  source: string;
  resultVariable: string;
  useRichDisplay: boolean;
  enablePreview: boolean;
  castDecimals: boolean;
  useNativeDates: boolean;
  loadIntoDataFrame: boolean;
  sqlCellOutputType: CellOutputType;
  allowDuplicateColumns: boolean;
  /**
   * perms on this are manually checked server side
   */
  connectionId: DataConnectionId | null;
  dataFrameCell?: boolean;
  sqlDisplayTableConfig: DisplayTableConfigPayload | null;
  cellReferencesV2: SqlCellReferencesV2 | null;
  cellReferencesParseError: CellReferencesParseError | null;
  sqlCellReferencesV3: StoredSqlReferencesV3 | null;
  jinjaCellReferencesV3: StoredCellReferencesV3 | null;
  jinjaSqlReferences: StoredJinjaSqlReferences | null;
}

export function createSqlCellPayload({
  castDecimals,
  cellReferencesParseError,
  cellReferencesV2,
  connectionId,
  dataFrameCell,
  enablePreview,
  id = uuid() as SqlCellId,
  jinjaCellReferencesV3,
  jinjaSqlReferences,
  loadIntoDataFrame,
  resultVariable,
  source,
  sqlCellReferencesV3,
  sqlDisplayTableConfig,
  useNativeDates,
  useRichDisplay,
}: {
  id?: SqlCellId;
  source?: string;
  resultVariable: string;
  useRichDisplay?: boolean;
  enablePreview?: boolean;
  sqlDisplayTableConfig?: DisplayTableConfigPayload | null;
  castDecimals: boolean;
  useNativeDates?: boolean;
  loadIntoDataFrame?: boolean;
  connectionId?: DataConnectionId | null;
  dataFrameCell?: boolean;
  cellReferencesV2?: SqlCellReferencesV2 | null;
  cellReferencesParseError?: CellReferencesParseError | null;
  sqlCellReferencesV3?: StoredSqlReferencesV3 | null;
  jinjaCellReferencesV3?: StoredCellReferencesV3 | null;
  jinjaSqlReferences?: StoredJinjaSqlReferences | null;
}): SqlCellPayload {
  return {
    type: CellType.SQL,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    source: source ?? "",
    resultVariable,
    castDecimals,
    useNativeDates: useNativeDates ?? true,
    useRichDisplay: useRichDisplay ?? false,
    enablePreview: enablePreview ?? true,
    connectionId: connectionId ?? null,
    dataFrameCell: dataFrameCell ?? false,
    sqlDisplayTableConfig: sqlDisplayTableConfig ?? null,
    loadIntoDataFrame: loadIntoDataFrame ?? true,
    cellReferencesV2: cellReferencesV2 ?? null,
    cellReferencesParseError: cellReferencesParseError ?? null,
    sqlCellReferencesV3: sqlCellReferencesV3 ?? null,
    jinjaCellReferencesV3: jinjaCellReferencesV3 ?? null,
    jinjaSqlReferences: jinjaSqlReferences ?? null,
    sqlCellOutputType: CellOutputType.PANDAS,
    allowDuplicateColumns: false,
  };
}

export interface VegaChartCellPayload {
  type: (typeof CellType)["VEGA_CHART"];
  id: VegaChartCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  vegaSpec: VegaLiteSpec | null;
  metadata: VegaMetadata | null;
  height: number | null;
  selectedLayerIndex: number;
  defaultInputTimezone: TimezoneName | null;
}

export function createVegaChartCellPayload({
  defaultInputTimezone = UTC,
  id = uuid() as VegaChartCellId,
  selectedDataFrameVariableName,
}: {
  id?: VegaChartCellId;
  defaultInputTimezone?: TimezoneName | null;
  /**
   * If provided, will intialize the cell with a vega-lite spec and metadata referencing this dataframe
   */
  selectedDataFrameVariableName?: string;
} = {}): VegaChartCellPayload {
  return {
    type: CellType.VEGA_CHART,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    vegaSpec:
      selectedDataFrameVariableName == null
        ? null
        : newDefaultTopLevelLayerSpec(),
    metadata:
      selectedDataFrameVariableName == null
        ? null
        : {
            byLayer: [
              {
                selectedDataFrameVariableName,
              },
            ],
          },
    height: null,
    selectedLayerIndex: 0,
    defaultInputTimezone,
  };
}

export interface MapCellPayload {
  type: (typeof CellType)["MAP"];
  id: MapCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  map: Map;
  height: number | null;
}

export function createMapCellPayload({
  id = uuid() as MapCellId,
}: {
  id?: MapCellId;
} = {}): MapCellPayload {
  return {
    type: CellType.MAP,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    map: {
      layers: [],
      theme: MapThemeHex.value,
    },
    height: MAP_DEFAULT_HEIGHT,
  };
}

export interface CreateChartCellMandatoryFields {
  resultVariable: string;
}

export interface ChartCellPayload {
  type: (typeof CellType)["CHART"];
  id: ChartCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  chartSpec: ChartSpec;
  chartSelection: ChartSelection | null;
  resultVariable: string;
  height: number | null;
  chartDisplayTableConfig: DisplayTableConfigPayload | null;
  colorMappings: ChartColorMappings;
  outputResult: boolean;
  displayType: ChartDisplayType;
}

/**
 * Creates an explore-backed chart cell payload from a chart spec.
 */
export function createChartCellPayload({
  chartSpec,
  createAsExplore = true,
  createDisplayTableConfig,
  displayTableConfig,
  id = uuid() as ChartCellId,
  outputResult,
  resultVariable,
}: {
  id?: ChartCellId;
  chartSpec?: ChartSpec;
  createAsExplore?: boolean;
  resultVariable?: string;
  createDisplayTableConfig?: boolean;
  outputResult?: boolean;
  displayTableConfig?: Partial<DisplayTableConfigPayload>;
} = {}): ChartCellPayload | ExploreCellPayload {
  const dataframes =
    chartSpec != null ? getChartReferencedDataFrames(chartSpec) : [];
  const isDataSourceTableConfig = dataframes.some(DataSourceTableConfig.guard);
  if (createAsExplore) {
    const exploreSpec = updateExploreSpecFromChartDiffs({
      curSpec: newSuggestibleChart(),
      newSpec:
        chartSpec ??
        newSuggestibleChart(dataframes.length > 0 ? dataframes[0] : undefined),
      exploreSpec: defaultExploreSpec({ visualizationType: "chart" }),
      fieldGroups: undefined,
    });
    return createExploreCellPayload({
      input: dataframes.length > 0 ? dataframes[0] : undefined,
      visualizationType: "chart",
      viewType: "visualization",
      spec: exploreSpec,
    });
  } else {
    return {
      type: CellType.CHART,
      id,
      createdDate: getDateTimeString(new Date()),
      updatedDate: getDateTimeString(new Date()),
      revision: 0,
      chartSpec: chartSpec ?? newSuggestibleChart(),
      chartSelection: null,
      resultVariable: resultVariable ?? DEFAULT_CHART_RESULT_VARIABLE,
      height: null,
      displayType: ChartDisplayType.CHART,
      chartDisplayTableConfig:
        displayTableConfig != null ||
        (createDisplayTableConfig ?? isDataSourceTableConfig)
          ? {
              pageSize: DISPLAY_TABLE_DEFAULTS.pageSize,
              hideIcons: false,
              hideIndex: false,
              defaultColumnWidth: null,
              sortDirectionDefault: SortDirection.ASC,
              columnProperties: [],
              pinIndexColumns: false,
              ...displayTableConfig,
              id: DisplayTableConfigId.check(uuid()),
              revision: 0,
            }
          : null,
      colorMappings: {},
      outputResult: outputResult ?? isDataSourceTableConfig,
    };
  }
}

export const ExploreCellPayload = RRecord({
  type: Literal(CellType.EXPLORE),
  id: ExploreCellId,
  dataframe: Union(ExploreCellDataframe, Null),
  createdDate: DateTimeString,
  updatedDate: DateTimeString,
  revision: Number,
  colorMappings: ChartColorMappings,
  displayTableConfig: DisplayTableConfigPayload,
  spec: ExploreSpec,
  semanticProjectId: Union(SemanticProjectId, Null),
  resultVariables: Array(String).asReadonly(),
  resultIncludeDetailColumns: Boolean,
  height: Union(Number, Null),
});

export type ExploreCellPayload = Static<typeof ExploreCellPayload>;

export const ExploreCellSemanticDatasetInputPayload = RRecord({
  semanticDatasetName: SemanticDatasetName,
  semanticProjectId: SemanticProjectId,
  dataConnectionId: DataConnectionId,
});
export type ExploreCellSemanticDatasetInputPayload = Static<
  typeof ExploreCellSemanticDatasetInputPayload
>;

export const ExploreCellSemanticViewInputPayload = RRecord({
  semanticViewName: SemanticViewName,
  semanticProjectId: SemanticProjectId,
  dataConnectionId: DataConnectionId,
});
export type ExploreCellSemanticViewInputPayload = Static<
  typeof ExploreCellSemanticViewInputPayload
>;

export type ExploreCellInput =
  | NoCodeCellDataframe
  | ExploreCellSemanticDatasetInputPayload
  | ExploreCellSemanticViewInputPayload;

export const getDefaultExploreDisplayTableConfig =
  (): DisplayTableConfigPayload => ({
    id: DisplayTableConfigId.check(uuid()),
    revision: 0,
    pageSize: DISPLAY_TABLE_DEFAULTS.pageSize,
    hideIcons: false,
    hideIndex: false,
    defaultColumnWidth: null,
    sortDirectionDefault: SortDirection.ASC,
    columnProperties: [],
    pinIndexColumns: false,
  });

export function createExploreCellPayload({
  colorMappings,
  displayTableConfig,
  id = uuid() as ExploreCellId,
  input,
  resultVariables = [],
  spec: initialSpec,
  viewType,
  visualizationType,
}: {
  id?: ExploreCellId;
  input?: ExploreCellInput;
  viewType?: ExploreViewType;
  displayTableConfig?: DisplayTableConfigPayload;
  spec?: ExploreSpec;
  visualizationType?: ExploreVisualizationType;
  colorMappings?: ChartColorMappings;
  resultVariables?: string[];
} = {}): ExploreCellPayload {
  const spec = {
    ...(initialSpec ?? defaultExploreSpec({ visualizationType })),
  };
  spec.viewType = viewType ?? spec.viewType;

  let dataframe: ExploreCellDataframe | null = null;
  let semanticProjectId: SemanticProjectId | null = null;
  if (NoCodeCellDataframe.guard(input)) {
    dataframe = input;
  } else if (ExploreCellSemanticDatasetInputPayload.guard(input)) {
    semanticProjectId = input.semanticProjectId;
    dataframe = {
      semanticDatasetName: input.semanticDatasetName,
      dataConnectionId: input.dataConnectionId,
      type: "SemanticDataset",
    };
  } else if (ExploreCellSemanticViewInputPayload.guard(input)) {
    semanticProjectId = input.semanticProjectId;
    dataframe = {
      semanticViewName: input.semanticViewName,
      dataConnectionId: input.dataConnectionId,
      type: "SemanticView",
    };
  }

  return {
    type: CellType.EXPLORE,
    id,
    dataframe: dataframe,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    displayTableConfig:
      displayTableConfig ?? getDefaultExploreDisplayTableConfig(),
    colorMappings: colorMappings ?? {},
    spec,
    semanticProjectId,
    resultVariables,
    resultIncludeDetailColumns: false,
    height: null,
  };
}

export interface WritebackCellPayload {
  type: (typeof CellType)["WRITEBACK"];
  id: WritebackCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  connectionId: DataConnectionId | null;
  /** @deprecated */
  connectionName: string | null;
  databaseName: string | null;
  schemaName: string | null;
  tableName: string | null;
  dataframeName: string | null;
  runInLogicView: boolean;
  runOnScheduledRun: boolean;
  runInApp: boolean;
  overwrite: boolean;
  dynamicTableName: boolean;
}

export function createWritebackCellPayload({
  dataframeName = null,
  id = uuid() as WritebackCellId,
}: {
  id?: WritebackCellId;
  dataframeName?: string | null;
} = {}): WritebackCellPayload {
  return {
    type: CellType.WRITEBACK,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    connectionId: null,
    connectionName: null,
    databaseName: null,
    schemaName: null,
    tableName: null,
    dataframeName,
    runInLogicView: false,
    runInApp: false,
    runOnScheduledRun: false,
    overwrite: true,
    dynamicTableName: false,
  };
}

export interface CreateDbtMetricCellMandatoryFields {
  // a new unique result variable that shouldn't conflict
  resultVariable: string;
  // the last used connection id
  connectionId: DataConnectionId | null;
  castDecimals: boolean;
}
export interface DbtMetricCellPayload {
  type: (typeof CellType)["DBT_METRIC"];
  id: DbtMetricCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  resultVariable: string;
  useRichDisplay: boolean;
  castDecimals: boolean;
  useNativeDates: boolean;
  secondaryCalculations: SecondaryCalculations[];
  /**
   * perms on this are manually checked server side
   */
  connectionId: DataConnectionId | null;
  dbtMetricDisplayTableConfig: DisplayTableConfigPayload | null;
  metricIds: DataSourceDbtMetricId[];
  selectedTimegrain: string | null;
  selectedDimensions: string[] | null;
  startDate: DateTimeString | null;
  endDate: DateTimeString | null;
}

export function createDbtMetricCellPayload({
  castDecimals,
  connectionId,
  displayTableConfigId = uuid() as DisplayTableConfigId,
  endDate,
  id = uuid() as DbtMetricCellId,
  metricIds = [],
  resultVariable,
  secondaryCalculations,
  selectedDimensions,
  selectedTimegrain,
  startDate,
  useRichDisplay,
}: {
  id?: DbtMetricCellId;
  displayTableConfigId?: DisplayTableConfigId;
  resultVariable: string;
  useRichDisplay?: boolean;
  castDecimals: boolean;
  connectionId?: DataConnectionId | null;
  metricIds?: DataSourceDbtMetricId[];
  selectedTimegrain?: string;
  selectedDimensions?: string[];
  secondaryCalculations?: SecondaryCalculations[];
  startDate?: DateTimeString | null;
  endDate?: DateTimeString | null;
}): DbtMetricCellPayload {
  return {
    type: CellType.DBT_METRIC,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    resultVariable,
    castDecimals,
    useNativeDates: true,
    secondaryCalculations: secondaryCalculations ?? [],
    useRichDisplay: useRichDisplay ?? false,
    connectionId: connectionId ?? null,
    metricIds,
    dbtMetricDisplayTableConfig: {
      id: displayTableConfigId,
      revision: 0,
      pageSize: DISPLAY_TABLE_DEFAULTS.pageSize,
      hideIcons: false,
      hideIndex: false,
      defaultColumnWidth: null,
      sortDirectionDefault: DISPLAY_TABLE_DEFAULTS.sortDirectionDefault,
      columnProperties: [],
      pinIndexColumns: false,
    },
    selectedTimegrain: selectedTimegrain ?? null,
    selectedDimensions: selectedDimensions ?? null,
    startDate: startDate ?? null,
    endDate: endDate ?? null,
  };
}

export interface CreatePivotCellMandatoryFields {
  resultVariable: string;
  castDecimals: boolean;
}

export interface PivotCellPayload {
  type: (typeof CellType)["PIVOT"];
  id: PivotCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  dataframe: NoCodeCellDataframe | null;
  resultVariable: string;
  pivotConfig: PivotTableConfig;
  displayTableConfig: DisplayTableConfigPayload;
  castDecimals: boolean;
}

/**
 * Will create a pivot cell payload or an explore cell payload depending on the createAsExplore arg.
 */

export function createPivotCellPayload({
  castDecimals,
  createAsExplore = true,
  dataframe = null,
  displayTableConfig,
  displayTableConfigId = uuid(),
  id = uuid() as PivotCellId,
  pivotConfig,
  resultVariable,
}: {
  id?: PivotCellId;
  dataframe?: NoCodeCellDataframe | null;
  resultVariable: string;
  displayTableConfigId?: string;
  displayTableConfig?: Partial<DisplayTableConfigPayload>;
  castDecimals: boolean;
  pivotConfig?: PivotTableConfig;
  createAsExplore?: boolean;
}): PivotCellPayload | ExploreCellPayload {
  return createAsExplore
    ? createExploreCellPayload({
        input: dataframe ?? undefined,
        visualizationType: "pivot-table",
      })
    : {
        type: CellType.PIVOT,
        id,
        createdDate: getDateTimeString(new Date()),
        updatedDate: getDateTimeString(new Date()),
        revision: 0,
        dataframe,
        resultVariable,
        castDecimals,
        pivotConfig: pivotConfig ?? {
          rows: [],
          columns: [],
          values: [],
        },
        displayTableConfig: {
          pageSize: DISPLAY_TABLE_DEFAULTS.pageSize,
          hideIcons: false,
          hideIndex: false,
          defaultColumnWidth: null,
          sortDirectionDefault: SortDirection.ASC,
          columnProperties: [],
          pinIndexColumns: true,
          ...displayTableConfig,
          id: displayTableConfigId as DisplayTableConfigId,
          revision: 0,
        },
      };
}

export interface CreateFilterCellMandatoryFields {
  resultVariable: string;
  castDecimals: boolean;
}

export interface FilterCellPayload {
  type: (typeof CellType)["FILTER"];
  id: FilterCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  dataframe: NoCodeCellDataframe | null;
  filters: FilterGroup | null;
  resultVariable: string;
  filterType: FilterType;
  appMode: FilterCellAppMode;
  castDecimals: boolean;
  useQueryMode: boolean;
  useNativeDates: boolean;
  cellOutputType: CellOutputType | null;
}

export function createFilterCellPayload({
  castDecimals,
  cellOutputType = CellOutputType.PANDAS,
  dataframe = null,
  emptyFilterGroup = false,
  id = uuid() as FilterCellId,
  resultVariable,
  useQueryMode,
}: {
  id?: FilterCellId;
  dataframe?: NoCodeCellDataframe | null;
  resultVariable: string;
  castDecimals: boolean;
  cellOutputType?: CellOutputType;
  useQueryMode?: boolean;
  emptyFilterGroup?: boolean;
}): FilterCellPayload {
  return {
    type: CellType.FILTER,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    dataframe,
    filters: emptyFilterGroup ? null : BASE_FILTER_GROUP,
    resultVariable,
    filterType: FilterType.KEEP,
    appMode: FilterCellAppMode.FULL_ACCESS,
    castDecimals,
    cellOutputType,
    useQueryMode: useQueryMode ?? false,
    useNativeDates: true,
  };
}

export interface ComponentImportCellPayload {
  type: (typeof CellType)["COMPONENT_IMPORT"];
  id: ComponentImportCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  componentVersionId: HexVersionId | null;
  /** @deprecated @see hexVersionIdToCopyFilesFrom */
  cellIdMapping?: Record<CellId, CellId>;
  hexVersionIdToCopyFilesFrom?: HexVersionId;
}

export function createComponentImportCellPayload({
  cellIdMapping,
  componentVersionId,
  hexVersionIdToCopyFilesFrom,
  id = uuid() as ComponentImportCellId,
}: {
  id?: ComponentImportCellId;
  componentVersionId?: HexVersionId;
  cellIdMapping?: Record<CellId, CellId>;
  hexVersionIdToCopyFilesFrom?: HexVersionId;
}): ComponentImportCellPayload {
  return {
    type: CellType.COMPONENT_IMPORT,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    componentVersionId: componentVersionId ?? null,
    cellIdMapping,
    hexVersionIdToCopyFilesFrom,
  };
}

export interface CollapsibleCellPayload {
  type: (typeof CellType)["COLLAPSIBLE"];
  id: CollapsibleCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  labelStyle: CollapsibleCellLabelStyle;
}

export function createCollapsibleCellPayload({
  id = uuid() as CollapsibleCellId,
  labelStyle = CollapsibleCellLabelStyle.AUTO,
}: {
  id?: CollapsibleCellId;
  labelStyle?: CollapsibleCellLabelStyle;
}): CollapsibleCellPayload {
  return {
    type: CellType.COLLAPSIBLE,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    labelStyle,
  };
}

export interface BlockCellPayload {
  type: (typeof CellType)["BLOCK"];
  id: BlockCellId;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  revision: number;
  blockConfig: BlockConfig | null;
}

export function createBlockCellPayload({
  blockConfig,
  id = uuid() as BlockCellId,
}: {
  id?: BlockCellId;
  blockConfig: BlockConfig | null;
}): BlockCellPayload {
  return {
    type: CellType.BLOCK,
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    blockConfig,
  };
}

export type CellContentsPayload =
  | CodeCellPayload
  | MarkdownCellPayload
  | TextCellPayload
  | DisplayTableCellPayload
  | InputCellPayload
  | SqlCellPayload
  | VegaChartCellPayload
  | CellGroupCellPayload
  | MetricCellPayload
  | MapCellPayload
  | WritebackCellPayload
  | DbtMetricCellPayload
  | PivotCellPayload
  | FilterCellPayload
  | ComponentImportCellPayload
  | CollapsibleCellPayload
  | ChartCellPayload
  | BlockCellPayload
  | ExploreCellPayload;

export interface StoryElementPayload {
  id: StoryElementId;
  visible: boolean;
  createdDate: DateTimeString;
  updatedDate: DateTimeString;
  showLabel: boolean;
  revision: number;
}

/**
 * The schema shape for a payload interface: every property present, and each one
 * parsing to the type of the field it stands for.
 *
 * `-?` strips the interface's own optional modifiers, so an optional field still
 * needs a schema rather than being droppable. An optional field then has to be
 * `ZodOptional` outright: `ZodType<X>` stays assignable to `ZodType<X | undefined>`,
 * so asking for the field's type alone would let a schema forget `.optional()` and
 * reject payloads at runtime instead.
 */
type ZodShapeFor<T> = {
  [K in keyof T]-?: undefined extends T[K]
    ? z.ZodOptional<z.ZodType<Exclude<T[K], undefined>, z.ZodTypeDef, unknown>>
    : z.ZodType<T[K], z.ZodTypeDef, unknown>;
};

/**
 * Runtime schemas for the cell content payloads above, used by `CREATE_CELL` and
 * `RESTORE_CELL`. Each mirrors the interface directly above its entry; `satisfies`
 * keeps them in step, so adding a property to a payload interface without a schema
 * for it, or giving a property a schema for the wrong type, stops compiling here.
 *
 * `z.union` rather than `z.discriminatedUnion` because `ExploreCellPayload` is a
 * runtype bridged with `zFromRuntype` and so is not a `ZodObject`. Every member still
 * pins `type` to its own literal, so at most one can match.
 */
const zCellPayloadBase = {
  createdDate: zDateTimeString,
  updatedDate: zDateTimeString,
  revision: z.number(),
};

const zCodeCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.CODE),
  id: zCodeCellId,
  source: z.string(),
  cellReferencesV2: zFromRuntype(PythonCellReferencesV2).nullable(),
  cellReferencesParseError: zFromRuntype(CellReferencesParseError).nullable(),
} satisfies ZodShapeFor<CodeCellPayload>);

const zMarkdownCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.MARKDOWN),
  id: zMarkdownCellId,
  source: z.string(),
  cellReferencesV2: zFromRuntype(CellReferencesV2).nullable(),
  cellReferencesParseError: zFromRuntype(CellReferencesParseError).nullable(),
} satisfies ZodShapeFor<MarkdownCellPayload>);

const zTextCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.TEXT),
  id: zTextCellId,
  richText: zFromRuntype(RichTextDocument),
  cellReferencesV2: zFromRuntype(CellReferencesV2).nullable(),
  cellReferencesParseError: zFromRuntype(CellReferencesParseError).nullable(),
} satisfies ZodShapeFor<TextCellPayload>);

const zCellGroupCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.CELL_GROUP),
  id: zCellGroupCellId,
  runOnAppLoad: z.boolean(),
} satisfies ZodShapeFor<CellGroupCellPayload>);

const zDisplayTableCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.DISPLAY_TABLE),
  id: zDisplayTableCellId,
  dataFrameVariableName: z.string().optional(),
  dataframe: zFromRuntype(NoCodeCellDataframe).nullable(),
  resultVariable: z.string(),
  displayTableConfig: zFromRuntype(DisplayTableConfigPayload),
} satisfies ZodShapeFor<DisplayTableCellPayload>);

const zMetricCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.METRIC),
  id: zMetricCellId,
  title: z.string().optional(),
  valueVariableName: z.string().optional(),
  displayFormat: zFromRuntype(MetricDisplayFormat).optional(),
  showComparison: z.boolean(),
  comparisonType: z.nativeEnum(MetricCellComparisonType).optional(),
  comparisonVariableName: z.string().optional(),
  comparisonLabel: z.string().optional(),
  comparisonFormat: z.nativeEnum(MetricCellComparisonFormat).optional(),
  valueRowIndex: z.number().optional(),
  valueColumn: z.string().optional(),
  valueAggregate: z.nativeEnum(HqlAggregationFunction).optional(),
  comparisonColumn: z.string().optional(),
  comparisonRowIndex: z.number().optional(),
  comparisonAggregate: z.nativeEnum(HqlAggregationFunction).optional(),
  valueResultVariable: z.string().optional(),
  comparisonResultVariable: z.string().optional(),
  outputResult: z.boolean().optional(),
  cellReferencesV2: zFromRuntype(CellReferencesV2).nullable(),
  cellReferencesParseError: zFromRuntype(CellReferencesParseError).nullable(),
} satisfies ZodShapeFor<MetricCellPayload>);

const zInputCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.INPUT),
  id: zParameterId,
  name: zParameterName,
  outputType: z.nativeEnum(ParameterOutputType),
  inputType: z.nativeEnum(ParameterInputType),
  options: zFromRuntype(ParameterOptions).nullable().optional(),
  defaultValueString: zJsonSerializedParameterValueContents.optional(),
  required: z.boolean(),
} satisfies ZodShapeFor<InputCellPayload>);

const zSqlCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.SQL),
  id: zSqlCellId,
  source: z.string(),
  resultVariable: z.string(),
  useRichDisplay: z.boolean(),
  enablePreview: z.boolean(),
  castDecimals: z.boolean(),
  useNativeDates: z.boolean(),
  loadIntoDataFrame: z.boolean(),
  sqlCellOutputType: z.nativeEnum(CellOutputType),
  allowDuplicateColumns: z.boolean(),
  connectionId: zDataConnectionId.nullable(),
  dataFrameCell: z.boolean().optional(),
  sqlDisplayTableConfig: zFromRuntype(DisplayTableConfigPayload).nullable(),
  cellReferencesV2: zFromRuntype(SqlCellReferencesV2).nullable(),
  cellReferencesParseError: zFromRuntype(CellReferencesParseError).nullable(),
  sqlCellReferencesV3: zFromRuntype(StoredSqlReferencesV3).nullable(),
  jinjaCellReferencesV3: zFromRuntype(StoredCellReferencesV3).nullable(),
  jinjaSqlReferences: zFromRuntype(StoredJinjaSqlReferences).nullable(),
} satisfies ZodShapeFor<SqlCellPayload>);

const zVegaChartCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.VEGA_CHART),
  id: zVegaChartCellId,
  // VegaLiteSpec is an external Vega-Lite type with no runtype; checked as an object.
  vegaSpec: zUnvalidatedObject<VegaLiteSpec>().nullable(),
  metadata: zFromRuntype(VegaMetadata).nullable(),
  height: z.number().nullable(),
  selectedLayerIndex: z.number(),
  defaultInputTimezone: zTimezoneName.nullable(),
} satisfies ZodShapeFor<VegaChartCellPayload>);

const zMapCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.MAP),
  id: zMapCellId,
  map: zFromRuntype(Map),
  height: z.number().nullable(),
} satisfies ZodShapeFor<MapCellPayload>);

const zChartCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.CHART),
  id: zChartCellId,
  chartSpec: zFromRuntype(ChartSpec),
  chartSelection: zFromRuntype(ChartSelection).nullable(),
  resultVariable: z.string(),
  height: z.number().nullable(),
  chartDisplayTableConfig: zFromRuntype(DisplayTableConfigPayload).nullable(),
  colorMappings: zFromRuntype(ChartColorMappings),
  outputResult: z.boolean(),
  displayType: z.nativeEnum(ChartDisplayType),
} satisfies ZodShapeFor<ChartCellPayload>);

const zWritebackCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.WRITEBACK),
  id: zWritebackCellId,
  connectionId: zDataConnectionId.nullable(),
  connectionName: z.string().nullable(),
  databaseName: z.string().nullable(),
  schemaName: z.string().nullable(),
  tableName: z.string().nullable(),
  dataframeName: z.string().nullable(),
  runInLogicView: z.boolean(),
  runOnScheduledRun: z.boolean(),
  runInApp: z.boolean(),
  overwrite: z.boolean(),
  dynamicTableName: z.boolean(),
} satisfies ZodShapeFor<WritebackCellPayload>);

const zDbtMetricCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.DBT_METRIC),
  id: zDbtMetricCellId,
  resultVariable: z.string(),
  useRichDisplay: z.boolean(),
  castDecimals: z.boolean(),
  useNativeDates: z.boolean(),
  secondaryCalculations: z.array(SecondaryCalculationsSchema),
  connectionId: zDataConnectionId.nullable(),
  dbtMetricDisplayTableConfig: zFromRuntype(
    DisplayTableConfigPayload,
  ).nullable(),
  metricIds: z.array(zDataSourceDbtMetricId),
  selectedTimegrain: z.string().nullable(),
  selectedDimensions: z.array(z.string()).nullable(),
  startDate: zDateTimeString.nullable(),
  endDate: zDateTimeString.nullable(),
} satisfies ZodShapeFor<DbtMetricCellPayload>);

const zPivotCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.PIVOT),
  id: zPivotCellId,
  dataframe: zFromRuntype(NoCodeCellDataframe).nullable(),
  resultVariable: z.string(),
  pivotConfig: zFromRuntype(PivotTableConfig),
  displayTableConfig: zFromRuntype(DisplayTableConfigPayload),
  castDecimals: z.boolean(),
} satisfies ZodShapeFor<PivotCellPayload>);

const zFilterCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.FILTER),
  id: zFilterCellId,
  dataframe: zFromRuntype(NoCodeCellDataframe).nullable(),
  filters: zFromRuntype(FilterGroup).nullable(),
  resultVariable: z.string(),
  filterType: z.nativeEnum(FilterType),
  appMode: z.nativeEnum(FilterCellAppMode),
  castDecimals: z.boolean(),
  useQueryMode: z.boolean(),
  useNativeDates: z.boolean(),
  cellOutputType: z.nativeEnum(CellOutputType).nullable(),
} satisfies ZodShapeFor<FilterCellPayload>);

const zComponentImportCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.COMPONENT_IMPORT),
  id: zComponentImportCellId,
  componentVersionId: zHexVersionId.nullable(),
  cellIdMapping: z.record(z.string(), zCellId).optional(),
  hexVersionIdToCopyFilesFrom: zHexVersionId.optional(),
} satisfies ZodShapeFor<ComponentImportCellPayload>);

const zCollapsibleCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.COLLAPSIBLE),
  id: zCollapsibleCellId,
  labelStyle: z.nativeEnum(CollapsibleCellLabelStyle),
} satisfies ZodShapeFor<CollapsibleCellPayload>);

const zBlockCellPayload = z.object({
  ...zCellPayloadBase,
  type: z.literal(CellType.BLOCK),
  id: zBlockCellId,
  blockConfig: zFromRuntype(BlockConfig).nullable(),
} satisfies ZodShapeFor<BlockCellPayload>);

const zCellPayloadByType: {
  readonly [K in CellType]: z.ZodType<{ type: K }, z.ZodTypeDef, unknown>;
} = {
  [CellType.CODE]: zCodeCellPayload,
  [CellType.MARKDOWN]: zMarkdownCellPayload,
  [CellType.TEXT]: zTextCellPayload,
  [CellType.DISPLAY_TABLE]: zDisplayTableCellPayload,
  [CellType.INPUT]: zInputCellPayload,
  [CellType.SQL]: zSqlCellPayload,
  [CellType.VEGA_CHART]: zVegaChartCellPayload,
  [CellType.CELL_GROUP]: zCellGroupCellPayload,
  [CellType.METRIC]: zMetricCellPayload,
  [CellType.MAP]: zMapCellPayload,
  [CellType.WRITEBACK]: zWritebackCellPayload,
  [CellType.DBT_METRIC]: zDbtMetricCellPayload,
  [CellType.PIVOT]: zPivotCellPayload,
  [CellType.FILTER]: zFilterCellPayload,
  [CellType.COMPONENT_IMPORT]: zComponentImportCellPayload,
  [CellType.COLLAPSIBLE]: zCollapsibleCellPayload,
  [CellType.CHART]: zChartCellPayload,
  [CellType.BLOCK]: zBlockCellPayload,
  [CellType.EXPLORE]: zFromRuntype(ExploreCellPayload),
};

/**
 * `z.union` rather than `z.discriminatedUnion` because `ExploreCellPayload` is a runtype
 * bridged with `zFromRuntype` and so is not a `ZodObject`. Every member still pins `type`
 * to its own literal, so at most one can match.
 */
export const zCellContentsPayload = z.union(
  Object.values(zCellPayloadByType) as [
    z.ZodTypeAny,
    z.ZodTypeAny,
    ...z.ZodTypeAny[],
  ],
) as z.ZodType<CellContentsPayload, z.ZodTypeDef, unknown>;

export const zStoryElementPayload = z.object({
  id: zStoryElementId,
  visible: z.boolean(),
  createdDate: zDateTimeString,
  updatedDate: zDateTimeString,
  showLabel: z.boolean(),
  revision: z.number(),
} satisfies ZodShapeFor<StoryElementPayload>);

export function createStoryElementPayload({
  id,
  visible,
}: {
  id: StoryElementId;
  visible?: boolean;
}): StoryElementPayload {
  return {
    id,
    createdDate: getDateTimeString(new Date()),
    updatedDate: getDateTimeString(new Date()),
    revision: 0,
    visible: visible ?? false,
    showLabel: true,
  };
}

const CREATE_CELL_TYPE = "CREATE_CELL" as const;
export const CreateCellPayload = z.object({
  cellId: zCellId,
  staticCellId: zStaticCellId,
  insertAfter: z.string().optional(),
  insertBefore: z.string().optional(),
  insertAt: z.string(),
  contents: zCellContentsPayload,
  storyElement: zStoryElementPayload,
  label: z.string().optional(),
  /** Will run the cell immediately, regardless, unless `forceDisableRunOnInsertion` is also set. */
  runAfterInsertion: z.boolean().optional(),
  /**
   * By default, some cells run automatically when created and others don't.
   * If this is set, the cell won't run when created regardless of other factors.
   * Overrides `runAfterInsertion` if both are true.
   */
  forceDisableRunOnInsertion: z.boolean().optional(),
  parentComponentImportCellId: zComponentImportCellId.optional(),
  parentCellId: zCellId.optional(),
  staticCellIdInSourceComponent: zStaticCellId.optional(),
  componentImportCellId: zComponentImportCellId.nullable().optional(),
  parentBlockCellId: zBlockCellId.optional(),
  blockCellId: zBlockCellId.nullable().optional(),
  excludeFromHistory: z.boolean().optional(),
  latestMagicEventId: zMagicEventId.nullable().optional(),
  isTemplate: z.boolean().optional(),
  origin: zCreateOriginMetadata.optional(),
});
export type CreateCellPayload = z.infer<typeof CreateCellPayload>;

export const CREATE_CELL = createHexVersionAtomicOperationDefinition({
  type: CREATE_CELL_TYPE,
  payloadSchema: CreateCellPayload,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
  },
  // we check perms on static cell id server side manually
  writeAuth: {
    kind: "and",
    and: [
      {
        kind: "hasSemanticCap",
        cap: SemanticCap.EDIT_PROJECT_CONTENTS,
      },
      {
        kind: "idArgDoesNotExist",
        idArg: "cellId",
        idType: "Cell",
      },
    ],
  },
  scopedIds: {
    cellId: { scope: "createdHere", idType: "Cell" },
    parentCellId: { scope: "ownedByWrapper", idType: "Cell" },
    parentComponentImportCellId: {
      scope: "ownedByWrapper",
      idType: "ComponentImportCell",
    },
    componentImportCellId: {
      scope: "ownedByWrapper",
      idType: "ComponentImportCell",
    },
    parentBlockCellId: {
      scope: "ownedByWrapper",
      idType: "BlockCell",
    },
    latestMagicEventId: {
      scope: "ownedByWrapper",
      idType: "MagicEvent",
    },
    blockCellId: {
      scope: "ownedByWrapper",
      idType: "BlockCell",
    },
  },
  logSafe: [
    "cellId",
    "staticCellId",
    "insertAfter",
    "insertBefore",
    "insertAt",
    "runAfterInsertion",
    "forceDisableRunOnInsertion",
    "parentComponentImportCellId",
    "componentImportCellId",
    "parentBlockCellId",
    "latestMagicEventId",
  ],
  conflictId: (op) => `${CREATE_CELL_TYPE}-${op.payload.cellId}`,
  excludeFromHistory: (op) =>
    op.payload.contents.type === "BLOCK" ||
    op.payload.parentBlockCellId != null ||
    !!op.payload.excludeFromHistory,
  excludeExtraOpsFromHistory: (op) =>
    op.payload.contents.type === "COMPONENT_IMPORT",
  /** @see CreateOriginMetadata */
  create: (args: CreateCellPayload) => ({
    type: CREATE_CELL_TYPE,
    payload: {
      ...args,
    },
  }),
});

export type CREATE_CELL = ExtractAtomicOperationFromDefinition<
  typeof CREATE_CELL
>;
